import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

/**
 * Track authentication events to Datadog for monitoring and debugging
 */
export default function ({ app }, inject) {
  const authAnalytics = {
    /**
     * Track login attempt
     */
    trackLoginAttempt (strategy) {
      const event = {
        event: 'auth_login_attempt',
        strategy,
        timestamp: new Date().toISOString(),
      }
      
      datadogLogs.logger.info('Auth login attempt', event)
      datadogRum.addAction('auth_login_attempt', event)
    },

    /**
     * Track successful login
     */
    trackLoginSuccess (strategy, duration) {
      const event = {
        event: 'auth_login_success',
        strategy,
        duration,
        timestamp: new Date().toISOString(),
      }
      
      datadogLogs.logger.info('Auth login success', event)
      datadogRum.addAction('auth_login_success', event)
      
      // Add custom metric for login duration
      if (duration) {
        datadogRum.addTiming('auth_login_duration', duration)
      }
    },

    /**
     * Track login failure
     */
    trackLoginFailure (strategy, error, duration) {
      const event = {
        event: 'auth_login_failure',
        strategy,
        error: error?.message || error,
        errorType: error?.response?.data?.error || 'unknown',
        statusCode: error?.response?.status,
        duration,
        timestamp: new Date().toISOString(),
      }
      
      datadogLogs.logger.error('Auth login failure', event)
      datadogRum.addAction('auth_login_failure', event)
      
      // Add error to RUM
      if (error) {
        datadogRum.addError(error, {
          source: 'auth',
          strategy,
        })
      }
    },

    /**
     * Track token exchange
     */
    trackTokenExchange (success, duration, error) {
      const event = {
        event: 'auth_token_exchange',
        success,
        duration,
        error: error?.message || error,
        timestamp: new Date().toISOString(),
      }
      
      if (success) {
        datadogLogs.logger.info('Auth token exchange success', event)
        datadogRum.addAction('auth_token_exchange_success', event)
        if (duration) {
          datadogRum.addTiming('auth_token_exchange_duration', duration)
        }
      } else {
        datadogLogs.logger.error('Auth token exchange failure', event)
        datadogRum.addAction('auth_token_exchange_failure', event)
        if (error) {
          datadogRum.addError(error, {
            source: 'auth',
            type: 'token_exchange',
          })
        }
      }
    },

    /**
     * Track token refresh
     */
    trackTokenRefresh (success, duration, error, retryCount) {
      const event = {
        event: 'auth_token_refresh',
        success,
        duration,
        retryCount,
        error: error?.message || error,
        timestamp: new Date().toISOString(),
      }
      
      if (success) {
        datadogLogs.logger.info('Auth token refresh success', event)
        datadogRum.addAction('auth_token_refresh_success', event)
        if (duration) {
          datadogRum.addTiming('auth_token_refresh_duration', duration)
        }
      } else {
        datadogLogs.logger.warn('Auth token refresh failure', event)
        datadogRum.addAction('auth_token_refresh_failure', event)
      }
    },

    /**
     * Track logout
     */
    trackLogout (success, error) {
      const event = {
        event: 'auth_logout',
        success,
        error: error?.message || error,
        timestamp: new Date().toISOString(),
      }
      
      if (success) {
        datadogLogs.logger.info('Auth logout success', event)
        datadogRum.addAction('auth_logout_success', event)
      } else {
        datadogLogs.logger.error('Auth logout failure', event)
        datadogRum.addAction('auth_logout_failure', event)
        if (error) {
          datadogRum.addError(error, {
            source: 'auth',
            type: 'logout',
          })
        }
      }
    },

    /**
     * Track callback processing
     */
    trackCallback (stage, duration, error) {
      const event = {
        event: 'auth_callback',
        stage,
        duration,
        error: error?.message || error,
        timestamp: new Date().toISOString(),
      }
      
      datadogLogs.logger.info(`Auth callback: ${stage}`, event)
      datadogRum.addAction(`auth_callback_${stage}`, event)
      
      if (duration) {
        datadogRum.addTiming(`auth_callback_${stage}_duration`, duration)
      }
      
      if (error) {
        datadogRum.addError(error, {
          source: 'auth',
          type: 'callback',
          stage,
        })
      }
    },

    /**
     * Track concurrent session events
     */
    trackSessionSync (action, success) {
      const event = {
        event: 'auth_session_sync',
        action,
        success,
        timestamp: new Date().toISOString(),
      }
      
      datadogLogs.logger.info('Auth session sync', event)
      datadogRum.addAction('auth_session_sync', event)
    },
  }

  // Inject into Vue instance
  inject('authAnalytics', authAnalytics)
}

